import React from "react";
import { Package, HeartPulse, Briefcase, Globe, ShieldCheck, Users, Flame, ArrowRight, MessageCircle } from "lucide-react";
import { BOOK_CALL_URL, WHATSAPP_URL } from "@/lib/constants";

const cases = [
  {
    num: "01",
    icon: Package,
    title: "Product Business",
    sub: "Baker · D2C Brand",
    now: "Price enquiries that ghost after one reply.",
    we: "Guided selection, price + next step clarity, auto follow-up.",
    impact: "Removes friction → more confirmed orders.",
  },
  {
    num: "02",
    icon: HeartPulse,
    title: "Clinic / Doctor",
    sub: "Healthcare practice",
    now: "No-shows, missed reminders, empty slots.",
    we: "Instant booking, slot confirmation, reminder & follow-up if missed.",
    impact: "More completed appointments, higher patient LTV.",
  },
  {
    num: "03",
    icon: Briefcase,
    title: "Service Business",
    sub: "Consultant · Agency",
    now: "Cold leads, long sales cycles, unclear close.",
    we: "Lead qualification, guided flow, nurture sequence, call booking.",
    impact: "Warmer leads, higher close rate.",
  },
  {
    num: "04",
    icon: Globe,
    title: "Website / E-commerce",
    sub: "Online store",
    now: "Visitors browse, abandon cart, disappear.",
    we: "WhatsApp capture on-site, cart reminders, time-limited nudges.",
    impact: "Recovers lost revenue, increases conversion.",
  },
  {
    num: "05",
    icon: ShieldCheck,
    title: "Financial Advisor",
    sub: "Insurance · Wealth",
    now: "Trust takes time — leads fade before deciding.",
    we: "Qualification, nurture, timed follow-ups, booked calls.",
    impact: "Trust built. Lead moves toward decision.",
  },
  {
    num: "06",
    icon: Users,
    title: "Existing Customer Base",
    sub: "Any business",
    now: "Past customers silent. Repeat revenue leaking.",
    we: "Re-engagement, offer reminders, occasion-based campaigns.",
    impact: "Activates dormant customers → higher LTV.",
  },
  {
    num: "07",
    icon: Flame,
    title: "High-Volume Enquiries",
    sub: "Real estate · Gyms · Coaching",
    now: "Flood of enquiries, team chaos, poor follow-up.",
    we: "Structured flow, lead tagging, follow-up automation.",
    impact: "Less chaos, better lead quality, better outcomes.",
  },
];

export default function UseCases() {
  return (
    <section
      id="use-cases"
      data-testid="usecases-section"
      className="relative py-20 lg:py-28"
      style={{ background: "#ffffff" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-[58px] leading-[1.1]"
            data-testid="usecases-headline"
          >
            See your business in these{" "}
            <span className="font-display-italic">use cases.</span>
          </h2>
          <p className="mt-5 text-lg" style={{ color: "var(--text-muted)" }}>
            Different industries. Same pattern. A structured WhatsApp system
            that plugs into how people already buy.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {cases.map((c, i) => {
            const Icon = c.icon;
            return (
              <article
                key={c.num}
                data-testid={`usecase-${i}`}
                className="group rounded-3xl p-7 border relative card-glow bg-white"
                style={{ borderColor: "var(--line)" }}
              >
                <div className="flex items-start justify-between">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center"
                    style={{
                      background: "rgba(212,164,76,0.14)",
                      color: "var(--gold-dark)",
                    }}
                  >
                    <Icon size={22} />
                  </div>
                  <span
                    className="font-display-italic text-2xl"
                    style={{ color: "var(--gold-dark)" }}
                  >
                    {c.num}
                  </span>
                </div>
                <h3 className="mt-6 font-display text-2xl leading-tight" style={{ color: "var(--ink-muted)" }}>
                  {c.title}
                </h3>
                <div
                  className="text-xs uppercase tracking-widest mt-1"
                  style={{ color: "var(--text-muted)" }}
                >
                  {c.sub}
                </div>

                <div className="mt-6 space-y-4">
                  <div>
                    <div
                      className="text-[11px] font-bold uppercase tracking-wider mb-1.5"
                      style={{ color: "#c94b4b" }}
                    >
                      Right now
                    </div>
                    <p className="text-[15px] text-[var(--ink)] leading-relaxed">
                      {c.now}
                    </p>
                  </div>
                  <div>
                    <div
                      className="text-[11px] font-bold uppercase tracking-wider mb-1.5"
                      style={{ color: "var(--whatsapp-dark)" }}
                    >
                      What we build
                    </div>
                    <p className="text-[15px] text-[var(--ink)] leading-relaxed">
                      {c.we}
                    </p>
                  </div>
                  <div
                    className="pt-4 border-t font-display-italic text-[15px]"
                    style={{
                      borderColor: "var(--line)",
                      color: "var(--ink)",
                    }}
                  >
                    → {c.impact}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Power Summary — eyebrow removed, spacing relaxed */}
        <div
          className="mt-20 rounded-[36px] p-10 lg:p-16 text-center relative overflow-hidden"
          style={{ background: "var(--ink)" }}
          data-testid="power-summary"
        >
          <div className="grain absolute inset-0" />
          <div className="relative">
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {["No new leads added", "No new ads run", "No extra effort"].map(
                (p) => (
                  <span
                    key={p}
                    className="px-5 py-2.5 rounded-full text-sm font-medium"
                    style={{
                      color: "rgba(248,241,231,0.85)",
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    {p}
                  </span>
                )
              )}
            </div>
            <h3
              className="mt-10 font-display text-white text-3xl sm:text-4xl lg:text-[48px] max-w-4xl mx-auto"
              style={{ lineHeight: 1.25 }}
            >
              Revenue isn’t lost because of a{" "}
              <span className="font-display-italic">lack of leads.</span>
              <br className="hidden sm:block" />
              It’s lost because of a{" "}
              <span className="font-display-italic">lack of follow-up</span> and
              structure.
            </h3>
          </div>
        </div>

        <div className="mt-14 flex flex-wrap justify-center gap-3">
          <a
            href={BOOK_CALL_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="usecases-cta"
            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm sm:text-base font-semibold transition-all hover:scale-[1.03]"
            style={{ background: "var(--gold)", color: "var(--ink)" }}
          >
            Book a Strategy Call
            <ArrowRight
              size={17}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="usecases-whatsapp-cta"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm sm:text-base font-semibold text-white transition-all hover:scale-[1.03]"
            style={{ background: "var(--whatsapp)" }}
          >
            <MessageCircle size={17} strokeWidth={2.5} />
            Chat on WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}
